import { useCallback, useEffect, useMemo, useState } from 'react'
import { collection, query, doc, serverTimestamp } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { addDoc, updateDoc, deleteDoc, onSnapshot } from '../lib/firestore'
import { db, storage } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { totemRefKey, type TotemRef } from './useTotemFollows'
import { runMutation } from '../lib/firestoreMutation'
import { track, type AnalyticsEvents } from '../lib/analytics'
import { useResetOnChange } from './useResetOnChange'
import { log } from '../lib/logger'
import type { Group, Totem, TotemBorderStyle } from '../types'
import { fbt } from 'fbtee'

const EMPTY_GROUPS: Map<string, Totem[]> = new Map()
const EMPTY_FOLLOWED: Map<string, Totem> = new Map()

export type TotemEntryPoint = NonNullable<AnalyticsEvents['group_totem_set']['entry_point']>

export interface TotemUpdates {
  emoji?: string
  name?: string
  borderStyle?: TotemBorderStyle
  editorIds?: string[]
  imageUrl?: string | null
}

// Single-group writes with no subscription of their own — for a caller that only
// creates (the festival-mode upsell) and already has the group's totems through
// useGroupsTotems, so it shouldn't open a second listener on the subcollection.
export function useGroupTotemMutations(groupId: string) {
  const createTotem = useCallback(
    async (
      emoji: string,
      name: string,
      borderStyle: TotemBorderStyle,
      userId: string,
      editorIds: string[],
      entryPoint: TotemEntryPoint,
    ) => {
      let newId: string | undefined
      await runMutation(fbt("Couldn't create totem", 'Error toast when creating a group totem fails'), async () => {
        const ref = await addDoc(collection(db, 'groups', groupId, 'totems'), {
          emoji,
          name,
          borderStyle,
          // Rules require the creator in the list; the picker locks their row.
          editorIds: editorIds.includes(userId) ? editorIds : [userId, ...editorIds],
          createdBy: userId,
          createdAt: serverTimestamp(),
        })
        newId = ref.id
        track('group_totem_set', { group_id: groupId, entry_point: entryPoint })
      })
      return newId
    },
    [groupId],
  )

  const updateTotem = useCallback(
    async (totemId: string, updates: TotemUpdates) => {
      await runMutation(fbt("Couldn't update totem", 'Error toast when editing a group totem fails'), async () => {
        await updateDoc(doc(db, 'groups', groupId, 'totems', totemId), updates)
        track('group_totem_set', { group_id: groupId })
      })
    },
    [groupId],
  )

  const uploadTotemImage = useCallback(
    async (totemId: string, blob: Blob) => {
      const ref = storageRef(storage, `groups/${groupId}/totem-${totemId}`)
      await uploadBytes(ref, blob, {
        contentType: blob.type,
        cacheControl: 'public, max-age=31536000, immutable',
      })
      const url = await getDownloadURL(ref)
      await updateDoc(doc(db, 'groups', groupId, 'totems', totemId), { imageUrl: url })
      return url
    },
    [groupId],
  )

  const removeTotem = useCallback(
    async (totemId: string) => {
      await runMutation(fbt("Couldn't remove totem", 'Error toast when deleting a group totem fails'), async () => {
        await deleteDoc(doc(db, 'groups', groupId, 'totems', totemId))
        track('group_totem_cleared', { group_id: groupId })
      })
    },
    [groupId],
  )

  return { createTotem, updateTotem, uploadTotemImage, removeTotem }
}

// Single-group CRUD — used by the totem management page.
export function useGroupTotems(groupId: string) {
  const ref = query(
    collection(db, 'groups', groupId, 'totems'),
  ) as Query<Totem>
  const { data: totems, loading, error } = useFirestoreCollection<Totem>(ref)
  const mutations = useGroupTotemMutations(groupId)

  return { totems, loading, error, ...mutations }
}

// Multi-group read-only subscription — subscribes to the totems subcollection
// for each group and merges into a flat lookup. Used by useTotemSlots to resolve
// totemId references on slot documents.
//
// `followedRefs` resolves totems the user follows but doesn't own: they live in
// a subcollection of a group the user isn't in, so they're fetched doc-by-doc
// rather than by listing a subcollection. They land in `totemsById` (which
// answers "what totem is this slot for") but deliberately NOT in `totemsByGroup`
// — that map feeds `eligibleTotems`, and following a totem must never confer the
// ability to move it.
export function useGroupsTotems(groups: Group[], followedRefs: TotemRef[] = []) {
  const [ownedTotems, setTotemsByGroup] = useState<Map<string, Totem[]>>(EMPTY_GROUPS)
  const [followed, setFollowedTotems] = useState<Map<string, Totem>>(EMPTY_FOLLOWED)

  const groupIds = useMemo(
    () =>
      groups
        .map(g => g.id)
        .sort()
        .join(','),
    [groups],
  )

  const followedKey = useMemo(
    () =>
      followedRefs
        .map(r => totemRefKey(r.groupId, r.totemId))
        .sort()
        .join(','),
    [followedRefs],
  )

  // Nothing to subscribe to means nothing to wait for. Keyed rather than reset
  // from the effect so the caller never sees a frame claiming to still be
  // loading a subscription that was already torn down.
  const [loading, setLoading] = useResetOnChange(groupIds, () => Boolean(groupIds))
  const [followedLoading, setFollowedLoading] =
    useResetOnChange(followedKey, () => Boolean(followedKey))

  // Masked rather than cleared on key change: the previous group's totems stay
  // painted while the new subscriptions warm up, avoiding an empty flash.
  const totemsByGroup = groupIds ? ownedTotems : EMPTY_GROUPS
  const followedTotems = followedKey ? followed : EMPTY_FOLLOWED

  useEffect(() => {
    if (!groupIds) return

    const ids = groupIds.split(',')
    const pending = new Set(ids)
    const results = new Map<string, Totem[]>()

    const unsubs = ids.map(gid =>
      onSnapshot(
        collection(db, 'groups', gid, 'totems'),
        snap => {
          results.set(
            gid,
            snap.docs.map(d => ({ id: d.id, ...d.data() }) as Totem),
          )
          pending.delete(gid)
          setTotemsByGroup(new Map(results))
          if (pending.size === 0) setLoading(false)
        },
        err => {
          log('totems', `group totems listener failed for ${gid}`, { code: err.code })
          pending.delete(gid)
          if (pending.size === 0) setLoading(false)
        },
      ),
    )

    return () => unsubs.forEach(u => u())
  }, [groupIds, setLoading])

  useEffect(() => {
    if (!followedKey) return

    const keys = followedKey.split(',')
    const pending = new Set(keys)
    const results = new Map<string, Totem>()

    const unsubs = keys.map(key => {
      const [gid, tid] = key.split('_')
      return onSnapshot(doc(db, 'groups', gid, 'totems', tid), snap => {
        // A totem deleted upstream drops out here, which correctly makes its
        // slots unresolvable and hides it — the stale follow doc is harmless.
        if (snap.exists()) results.set(key, { id: snap.id, ...snap.data() } as Totem)
        else results.delete(key)
        pending.delete(key)
        setFollowedTotems(new Map(results))
        if (pending.size === 0) setFollowedLoading(false)
      }, err => {
        log('totems', `followed totem listener failed for ${key}`, { code: err.code })
        pending.delete(key)
        if (pending.size === 0) setFollowedLoading(false)
      })
    })

    return () => unsubs.forEach(u => u())
  }, [followedKey, setFollowedLoading])

  const totemsById = useMemo(() => {
    const map = new Map<string, Totem>()
    for (const totems of totemsByGroup.values()) {
      for (const t of totems) map.set(t.id, t)
    }
    for (const t of followedTotems.values()) map.set(t.id, t)
    return map
  }, [totemsByGroup, followedTotems])

  return { totemsByGroup, totemsById, loading: loading || followedLoading }
}
